import React, { useContext, ReactNode, JSX } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthContext } from 'context/AuthProvider';
import Layout from 'containers/Layout/Layout';
import Loader from 'components/Loader/Loader';
import {
  HOME_PAGE,
  // LISTING_POSTS_PAGE,
  // SINGLE_POST_PAGE,
  AGENT_PROFILE_PAGE,
  AGENT_PROFILE_FAVORITE,
  AGENT_PROFILE_CONTACT,
  PRICING_PLAN_PAGE,
  PRIVACY_PAGE,
  LOGIN_PAGE,
  REGISTRATION_PAGE,
  FORGET_PASSWORD_PAGE,
  ADD_HOTEL_PAGE,
  AGENT_IMAGE_EDIT_PAGE,
  AGENT_PASSWORD_CHANGE_PAGE,
  AGENT_ACCOUNT_SETTINGS_PAGE,
  CHAT_PAGE,
  PROPERTY_SOURCE_PAGE,
  HOST_MESSAGES_PAGE,
} from './settings/constant';
import MobileBottomNav from 'components/UI/MobileBottomNav/MobileBottomNav';
import TitleAndDescription from 'containers/TitleAndDescription/TitleAndDescription';
import Amenities from 'containers/Amenities/Amenities';
import PhotoUpload from 'containers/PhotoUpload/PhotoUpload';
import ChooseThumbnail from 'containers/ChooseThumbnail/ChooseThumbnail';
import BookingSettings from 'containers/BookingSettings/BookingSettings';
import HouseRules from 'containers/HouseRules/HouseRules';
import HostingPlan from 'containers/HostingPlan/HostingPlan';
import ImportListing from 'containers/ImportListing/ImportListing';
import Confirmation from 'containers/Confirmation/Confirmation';
import SearchForm from 'components/SearchForm/SearchForm';
import PaymentPage from 'containers/PaymentPage/PaymentPage';
import PaymentResponse from 'containers/PaymentResponse/PaymentResponse';
import Bookings from 'containers/PropertyBookings/Bookings';
import CouponList from 'containers/CouponsPage/CouponList';
import CouponFormPage from 'containers/CouponsPage/CouponFormPage';
const GstDetails = React.lazy(() => import('containers/GstDetails/GstDetails'));
const HostProfile = React.lazy(() => import('containers/HostProfile/HostProfile'));
const HostProCelebration = React.lazy(
  () => import('containers/HostProCelebration/HostProCelebration'),
);
const Analytics = React.lazy(() => import('containers/Analytics/Analytics'));
const ActivePlans = React.lazy(() => import('containers/ActivePlans/ActivePlans'));
const DealsPage = React.lazy(
  () => import('containers/DealsPage/DealsPage'),
);

// protected route
interface RequireAuthProps {
  children: ReactNode;
}

/** Old /host/house and /host/property-offerings links land on the combined first step. */
function RedirectToDescription(): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/host/description', search }} replace />;
}

/** Old thumbnail / video links land on the combined Photos & video step. */
function RedirectToPhotos(): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/host/photo-upload', search }} replace />;
}

function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  let { loggedIn } = useContext(AuthContext) as any;
  let location = useLocation();
  const hasToken =
    typeof window !== 'undefined' && !!localStorage.getItem('jwtToken');
  if (!loggedIn && !hasToken) {
    const returnTo = location.pathname + location.search + location.hash;
    localStorage.setItem('redirectAfterLogin', returnTo);
    return <Navigate to={LOGIN_PAGE} state={{ from: location }} />;
  }

  return children as JSX.Element;
}

function RequireAdmin({ children }: RequireAuthProps): JSX.Element {
  const auth = useContext(AuthContext) as any;
  const location = useLocation();
  const rolesFromContext: string[] = auth?.user?.roles || [];

  const parseJwtPayload = (token: string | null): any | null => {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const json = atob(padded);
      return JSON.parse(json);
    } catch (_) {
      return null;
    }
  };

  const isAdminFromRoles = (roles: any): boolean => {
    const arr = Array.isArray(roles) ? roles : roles ? [roles] : [];
    return arr
      .map((r: any) => String(r || '').toUpperCase())
      .some((r: string) => r === 'ADMIN' || r === 'ROLE_ADMIN' || r.includes('ADMIN'));
  };

  let userDetails: any = null;
  try {
    const raw = localStorage.getItem('userDetails');
    userDetails = raw ? JSON.parse(raw) : null;
  } catch (_) {
    userDetails = null;
  }

  const jwtPayload = parseJwtPayload(
    typeof window !== 'undefined' ? localStorage.getItem('jwtToken') : null,
  );

  const isAdmin =
    isAdminFromRoles(rolesFromContext) ||
    isAdminFromRoles(userDetails?.roles) ||
    isAdminFromRoles(userDetails?.role) ||
    isAdminFromRoles(userDetails?.authorities) ||
    String(userDetails?.userType || userDetails?.type || '')
      .toUpperCase()
      .includes('ADMIN') ||
    userDetails?.isAdmin === true ||
    isAdminFromRoles(jwtPayload?.roles) ||
    isAdminFromRoles(jwtPayload?.role) ||
    isAdminFromRoles(jwtPayload?.authorities);

  if (!isAdmin) {
    return <Navigate to={HOME_PAGE} state={{ from: location }} replace />;
  }
  return children as JSX.Element;
}

// public routes
// const HomePage = React.lazy(() => import('containers/Home/Home'));

// const ListingPage = React.lazy(() => import('containers/Listing/Listing'));
const SinglePageView = React.lazy(
  () => import('containers/SinglePage/SinglePageView'),
);
const AgentDetailsViewPage = React.lazy(
  () => import('containers/Agent/AccountDetails/AgentDetailsViewPage'),
);
const AgentItemLists = React.lazy(
  () => import('containers/Agent/AccountDetails/AgentItemLists'),
);
const AgentFavItemLists = React.lazy(
  () => import('containers/Agent/AccountDetails/AgentFavItemLists'),
);
const AgentContact = React.lazy(
  () => import('containers/Agent/AccountDetails/AgentContact'),
);
const PricingPage = React.lazy(() => import('containers/Pricing/Pricing'));
const PrivacyPage = React.lazy(() => import('containers/Privacy/Privacy'));

// const LandingPage = React.lazy(() => import('containers/Landing/Landing'));

// const OnBoarding = React.lazy(
//   () => import('containers/TravellerOnboarding/TravellerOnboarding'),
// );

// const Property = React.lazy(()=>import('containers/property/Property'));

const Property = React.lazy(
  () => import('containers/Property/PropertyDetails'),
);

const Structure = React.lazy(() => import('containers/Structure/Structure'));

const PropertyListing = React.lazy(
  () => import('containers/PropertyListing/PropertyListingModern'),
);


const Location = React.lazy(() => import('containers/Location/Location'));

// const Property = import('containers/Property/PropertyDetails');

const SignInPage = React.lazy(() => import('containers/Auth/SignIn/SignIn'));
const SignUpPage = React.lazy(() => import('containers/Auth/SignUp/SignUp'));
const ForgetPasswordPage = React.lazy(
  () => import('containers/Auth/ForgetPassword'),
);
const NotFound = React.lazy(() => import('containers/404/404'));
// protected route
const AddListingPage = React.lazy(
  () => import('containers/AddListing/AddListing'),
);

const MyBiddingsPage = React.lazy(
  () => import('containers/MyBiddings/MyBiddings'),
);

const HostAdmin = React.lazy(
  () => import('containers/AdminDashboard/TransactionTable'),
);

const HostDetail = React.lazy(
  () => import('containers/AdminDashboard/HostTable'),
);

const HostCalculator = React.lazy(
  () => import('containers/HostCalculator/HostCalculator'),
);
const HostCalendar = React.lazy(
  () => import('containers/HostCalendar/HostCalendar'),
);

const PendingApproval = React.lazy(
  () => import('containers/AdminDashboard/PendingApproval'),
);

const UnpaqdReviews = React.lazy(
  () => import('containers/AdminDashboard/UnpaqdReviews'),
);
const ReviewStats = React.lazy(
  () => import('containers/AdminDashboard/ReviewStats'),
);
const PerfectStayFeedback = React.lazy(
  () => import('containers/AdminDashboard/PerfectStayFeedback'),
);

// const GstDetails = React.lazy(
//   () => import('containers/GstDetails/GstDetails'),
// );

const AgentAccountSettingsPage = React.lazy(
  () => import('containers/Agent/AccountSettings/AgentAccountSettingsPage'),
);
const AgentCreateOrUpdateForm = React.lazy(
  () => import('containers/Agent/AccountSettings/AgentCreateOrUpdateForm'),
);
const AgentPictureChangeForm = React.lazy(
  () => import('containers/Agent/AccountSettings/AgentPictureChangeForm'),
);
const ChangePassWord = React.lazy(
  () => import('containers/Agent/AccountSettings/ChangePassWordForm'),
);

const PrivacyPolicy = React.lazy(() => import('components/PrivacyPolicy'));

const PaymentTerms = React.lazy(() => import('components/PaymentTerms'));

const TermOfUse = React.lazy(() => import('components/TermOfUse'));
const AboutUs = React.lazy(() => import('components/AboutUs'));

const ChatPage = React.lazy(
  () => import('containers/CometChat/ChatPageWrapper'),
);

const HostMessagesPage = React.lazy(
  () => import('containers/HostMessages/HostMessagesPage'),
);

const AffiliateList = React.lazy(
  () => import('containers/Affiliates/AffiliateList'),
);

const HostAudit = React.lazy(
  () => import('containers/AdminDashboard/HostAudit'),
);

const PropertyDeactivate = React.lazy(
  () => import('containers/AdminDashboard/PropertyDeactivate'),
);

const PropertyActivate = React.lazy(
  () => import('containers/AdminDashboard/PropertyActivate'),
);

const PropertyUnsyncAirbnb = React.lazy(
  () => import('containers/PropertyUnsyncAirbnb/PropertyUnsyncAirbnb'),
);

const PropertyPriceLabsSync = React.lazy(
  () => import('containers/PropertyPriceLabsSync/PropertyPriceLabsSync'),
);

const DeactivateByPhone = React.lazy(
  () => import('containers/AdminDashboard/DeactivateByPhone'),
);

const BookingConfirmationPage = React.lazy(
  () => import('containers/BookingConfirmation/BookingConfirmationPage'),
);
const BrochuresPage = React.lazy(() => import('containers/AdminDashboard/BrochuresPage'));
const OrphanNightsPage = React.lazy(() => import('containers/AdminDashboard/OrphanNightsPage'));
const GenerateBrochurePage = React.lazy(
  () => import('containers/AdminDashboard/GenerateBrochurePage'),
);

const BookingPriceBreakup = React.lazy(
  () => import('containers/AdminDashboard/BookingPriceBreakup'),
);

const SendNotification = React.lazy(
  () => import('containers/AdminDashboard/SendNotification'),
);

const AssignPropertyToHost = React.lazy(
  () => import('containers/AdminDashboard/AssignPropertyToHost'),
);

const UnassignPropertyFromHost = React.lazy(
  () => import('containers/AdminDashboard/UnassignPropertyFromHost'),
);

const ManageHostPlans = React.lazy(
  () => import('containers/AdminDashboard/ManageHostPlans'),
);
const ManageUniqueStays = React.lazy(
  () => import('containers/AdminDashboard/ManageUniqueStays'),
);

const WalkthroughRequests = React.lazy(
  () => import('containers/AdminDashboard/WalkthroughRequests'),
);

const LeadsDashboard = React.lazy(
  () => import('containers/AdminDashboard/LeadsDashboard'),
);

const LowerPriceRequests = React.lazy(
  () => import('containers/AdminDashboard/LowerPriceRequests'),
);

const HotelRateRequests = React.lazy(
  () => import('containers/AdminDashboard/HotelRateRequests'),
);

const SyncFailuresDashboard = React.lazy(
  () => import('containers/AdminDashboard/SyncFailuresDashboard'),
);

const HostFinancialDetails = React.lazy(
  () => import('containers/AdminDashboard/HostFinancialDetails'),
);

const HotelBookingsDashboard = React.lazy(
  () => import('containers/AdminDashboard/HotelBookingsDashboard'),
);

const HostLandingPage = React.lazy(
  () => import('pages/HostLandingPage/HostLandingPage'),
);

const PropertySourceDashboard = React.lazy(
  () => import('containers/AdminDashboard/PropertySourceDashboard'),
);

const AdminChatsPage = React.lazy(
  () => import('containers/AdminDashboard/AdminChatsPage'),
);

// Always land at the top of a newly opened screen (e.g. after tapping Continue
// at the bottom of the previous onboarding step).
function ScrollToTopOnRouteChange(): null {
  const { pathname } = useLocation();
  React.useLayoutEffect(() => {
    const toTop = (): void => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    toTop();
    // Once more after the new screen has painted, in case it mounted late (lazy routes).
    const frame = window.requestAnimationFrame(toTop);
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);
  return null;
}

export default function AppRoutes(): JSX.Element {
  return (
    <>
      <ScrollToTopOnRouteChange />
      <Routes>
        <Route
          index
          element={
            <RequireAuth>
              <React.Suspense fallback={<Loader className="" />}>
                <Property />
              </React.Suspense>
            </RequireAuth>
          }
        />
        <Route path={HOME_PAGE} element={<Layout />}>
        {/* <Route
          path={LISTING_POSTS_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <ListingPage />
            </React.Suspense>
          }
        />
        <Route
          path={`${SINGLE_POST_PAGE}/:slug`}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <SinglePageView />
            </React.Suspense>
          }
        /> */}
        {/* Nested routes for agent page */}
        <Route
          path={AGENT_PROFILE_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <AgentDetailsViewPage />
            </React.Suspense>
          }
        >
          <Route
            path={AGENT_PROFILE_PAGE}
            element={
              <React.Suspense fallback={<Loader className="" />}>
                <AgentItemLists />
              </React.Suspense>
            }
          />
          <Route
            path={AGENT_PROFILE_FAVORITE}
            element={
              <React.Suspense fallback={<Loader className="" />}>
                <AgentFavItemLists />
              </React.Suspense>
            }
          />
          <Route
            path={AGENT_PROFILE_CONTACT}
            element={
              <React.Suspense fallback={<Loader className="" />}>
                <AgentContact />
              </React.Suspense>
            }
          />
        </Route>
        {/* end of Nested routes */}
        <Route
          path={PRICING_PLAN_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <PricingPage />
            </React.Suspense>
          }
        />
        <Route
          path={PRIVACY_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <PrivacyPage />
            </React.Suspense>
          }
        />
        <Route
          path={LOGIN_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <SignInPage />
            </React.Suspense>
          }
        />

        <Route
          path={REGISTRATION_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <SignUpPage />
            </React.Suspense>
          }
        />

        <Route
          path={FORGET_PASSWORD_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <ForgetPasswordPage />
            </React.Suspense>
          }
        />

        <Route
          path={CHAT_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <ChatPage />
            </React.Suspense>
          }
        />

        <Route
          path={HOST_MESSAGES_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <RequireAuth>
                <HostMessagesPage />
              </RequireAuth>
            </React.Suspense>
          }
        />
        <Route
          path={`${HOST_MESSAGES_PAGE}/:id`}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <RequireAuth>
                <HostMessagesPage />
              </RequireAuth>
            </React.Suspense>
          }
        />
        {/* Protected routes */}
        <Route
          path={ADD_HOTEL_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <RequireAuth>
                <AddListingPage />
              </RequireAuth>
            </React.Suspense>
          }
        />
        <Route
          path={AGENT_ACCOUNT_SETTINGS_PAGE}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <RequireAuth>
                <AgentAccountSettingsPage />
              </RequireAuth>
            </React.Suspense>
          }
        >
          <Route
            path={AGENT_ACCOUNT_SETTINGS_PAGE}
            element={
              <React.Suspense fallback={<Loader className="" />}>
                <AgentCreateOrUpdateForm />
              </React.Suspense>
            }
          />
          <Route
            path={AGENT_IMAGE_EDIT_PAGE}
            element={
              <React.Suspense fallback={<Loader className="" />}>
                <AgentPictureChangeForm />
              </React.Suspense>
            }
          />
          <Route
            path={AGENT_PASSWORD_CHANGE_PAGE}
            element={
              <React.Suspense fallback={<Loader className="" />}>
                <ChangePassWord />
              </React.Suspense>
            }
          />
        </Route>
        {/* end of Protected routes*/}
        <Route
          path="*"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <NotFound />
            </React.Suspense>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <PrivacyPolicy />
            </React.Suspense>
          }
        />
        <Route
          path="/payment-terms"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <PaymentTerms />
            </React.Suspense>
          }
        />
        <Route
          path="/term-of-use"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <TermOfUse />
            </React.Suspense>
          }
        />
        <Route
          path="/about-us"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <AboutUs />
            </React.Suspense>
          }
        />
        <Route
          path={`/property/:slug/:title`}
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <SinglePageView />
            </React.Suspense>
          }
        />
        <Route
          path="/customer/my-bids"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <MyBiddingsPage userType={'CUSTOMER'} />
            </React.Suspense>
          }
        />

        <Route
          path="/paymentConfirmation"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <PaymentPage />
            </React.Suspense>
          }
        />

        <Route
          path="/bookingConfirmation"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <PaymentResponse />
            </React.Suspense>
          }
        />

        <Route
          path="/customer/myBookings"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <Bookings userType={'CUSTOMER'} />
            </React.Suspense>
          }
        />

        <Route
          path="/host/myBookings"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <Bookings userType={'HOST'} />
            </React.Suspense>
          }
        />

        <Route
          path="/host/my-bids"
          element={
            <React.Suspense fallback={<Loader className="" />}>
              <MyBiddingsPage userType={'HOST'} />
            </React.Suspense>
          }
        />
      </Route>

      {/* <Route
        path="/host/property-registration"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <Property />
          </React.Suspense>
        }
      /> */}

      <Route
        path="/host/description"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <Structure />
          </React.Suspense>
        }
      />
      {/* The old House step now lives on the combined first page. */}
      <Route path="/host/house" element={<RedirectToDescription />} />
      <Route
        path="/host/booking-settings"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <BookingSettings />
          </React.Suspense>
        }
      />
      <Route
        path="/host/house-rules"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HouseRules />
          </React.Suspense>
        }
      />
      <Route
        path="/host/hosting-plan"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HostingPlan />
          </React.Suspense>
        }
      />
      <Route
        path="/host/import-listing"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <ImportListing />
          </React.Suspense>
        }
      />
      <Route
        path="/host/gst-details"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <GstDetails />
          </React.Suspense>
        }
      />
      <Route
        path="/host/acknowledgement"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <Confirmation />
          </React.Suspense>
        }
      />
      <Route
        path="/host/location"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <Location />
          </React.Suspense>
        }
      />
      <Route path="/host/property-offerings" element={<RedirectToDescription />} />
      <Route
        path="/host/title-and-description"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <TitleAndDescription />
          </React.Suspense>
        }
      />
      <Route
        path="/host/amenities"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <Amenities />
          </React.Suspense>
        }
      />
      <Route
        path="/host/property-photos"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <PhotoUpload />
          </React.Suspense>
        }
      />
      {/* Video is part of the combined Photos & video step; the thumbnail is picked on its own next screen. */}
      <Route path="/host/property-video" element={<RedirectToPhotos />} />
      <Route
        path="/host/select-thumbnail"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <ChooseThumbnail />
          </React.Suspense>
        }
      />
      <Route
        path="/host/photo-upload"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <PhotoUpload />
          </React.Suspense>
        }
      />
      <Route
        path="/host/property-listing"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <PropertyListing />
          </React.Suspense>
        }
      />
      <Route
        path="/host/calculator"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HostCalculator />
          </React.Suspense>
        }
      />
      <Route
        path="/host/calendar"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HostCalendar />
          </React.Suspense>
        }
      />
      <Route
        path="/host/profile"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <HostProfile />
            </RequireAuth>
          </React.Suspense>
        }
      />
      <Route
        path="/host/pro/select-listing"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <HostProCelebration />
            </RequireAuth>
          </React.Suspense>
        }
      />
      <Route
        path="/host/analytics"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <Analytics />
            </RequireAuth>
          </React.Suspense>
        }
      />
      <Route path="/host/coupons" element={<CouponList />} />
      <Route path="/host/coupons/new" element={<CouponFormPage />} />
      <Route path="/host/coupons/:id/edit" element={<CouponFormPage />} />
      <Route
        path="/host/deals"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <DealsPage />
          </React.Suspense>
        }
      />
      <Route
        path="/host/active-plans"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <ActivePlans />
          </React.Suspense>
        }
      />
      <Route
        path="/host/affiliates"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <AffiliateList />
          </React.Suspense>
        }
      />

      <Route
        path="/host/audit"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <HostAudit />
            </RequireAuth>
          </React.Suspense>
        }
      />
      {/* <Route
        path="/landing"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <LandingPage></LandingPage>
          </React.Suspense>
        }
      /> */}
      {/* <Route
        path="/onboard"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <OnBoarding></OnBoarding>
          </React.Suspense>
        }
      /> */}
      <Route
        path="/search-location"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <SearchForm />
          </React.Suspense>
        }
      />
      <Route
        path="/b23y@s"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HostAdmin />
          </React.Suspense>
        }
      />
      <Route
        path="/b24y@s"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HostDetail />
          </React.Suspense>
        }
      />
      <Route
        path="/admin/manage-property"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <PropertyDeactivate />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/activate-property"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <PropertyActivate />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/host/unsync-airbnb-property"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <PropertyUnsyncAirbnb />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/host/pricelabs-sync"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <PropertyPriceLabsSync />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/deactivate-account"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <DeactivateByPhone />
          </React.Suspense>
        }
      />

      <Route
        path="/admin/pending-approval"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <PendingApproval />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/generate-voucher"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <BookingConfirmationPage />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/brochures"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <BrochuresPage />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/orphan-nights"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <OrphanNightsPage />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/generate-brochure"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <GenerateBrochurePage />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/unpaqd-reviews"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <UnpaqdReviews />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/review-stats"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <ReviewStats />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/perfect-stay-feedback"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <PerfectStayFeedback />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/booking-price-breakup"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <BookingPriceBreakup />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/send-notification"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <SendNotification />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/assign-property-host"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <AssignPropertyToHost />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/unassign-property-host"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <UnassignPropertyFromHost />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/unique-stays"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <ManageUniqueStays />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/manage-host-plans"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <ManageHostPlans />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/walkthrough-requests"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <WalkthroughRequests />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/leads"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <LeadsDashboard />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path={PROPERTY_SOURCE_PAGE}
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <PropertySourceDashboard />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/lower-price-requests"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <LowerPriceRequests />
          </React.Suspense>
        }
      />

      <Route
        path="/admin/hotel-rate-requests"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HotelRateRequests />
          </React.Suspense>
        }
      />

      <Route
        path="/admin/host-financial-details"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <HostFinancialDetails />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/sync-failures"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <SyncFailuresDashboard />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/hotel-bookings"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAuth>
              <HotelBookingsDashboard />
            </RequireAuth>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/chats"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <AdminChatsPage />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/admin/chats/:id"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <RequireAdmin>
              <AdminChatsPage />
            </RequireAdmin>
          </React.Suspense>
        }
      />

      <Route
        path="/host-landing"
        element={
          <React.Suspense fallback={<Loader className="" />}>
            <HostLandingPage />
          </React.Suspense>
        }
      />

    </Routes>
    </>
  );
}
